import React, {
    useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import moment from 'moment-timezone';
import Cookies from 'js-cookie';
import _ from 'lodash';
import { DateObject } from 'react-multi-date-picker';
import { useOutletContext } from 'react-router';
import {
    Autocomplete, Chip, CircularProgress, TextField, ThemeProvider, createTheme,
} from '@mui/material';
import { useApiData } from '../../../config/reducer/Modules';
import { APIGetDevices } from '../../../config/restAPI/FetchAPI';
import ListNavigationTab from '../../../component/listNavigationTab/ListNavigationTab';
import Title from '../../../component/title/Title';
import ConfigTab from '../../../component/configurationTab/configurationTab';
import {
    CreateWithIcon, ExportCSVButton, LiveMapButton,
} from '../../../component/buttonComponent/ButtonComponent';
import SnackBar from '../../../component/snackbar/Snackbar';
import DateRangePicker from '../../../component/dateRangePicker/DateRangePicker';
import { downloadCSV } from '../../../config/function/exportCSV';
import { AGENCY_TIMEZONE_COOKIE, resolveAgencyTimezone } from '../../../config/function/agencyTimezone';
import { MAP_THEMES, defaultMapTheme } from '../../../config/function/mapStyles';
import { OD_TELEMETRY_ENGINES_ENABLED, OD_TRIP_TAPE_ENABLED } from '../../../config/featureFlags';
import {
    OD_MAX_ASSETS,
    failedAssets,
    fetchAnalyticsForAssets,
    fetchTrailForTrip,
    fetchTripsForAssets,
    flattenTrips,
    mergeAnalytics,
} from './odAnalyticsFetch';
import {
    buildFocus,
    buildTripNumbers,
    filterTripsByDay,
    filterTripsByPair,
    filterTripsByRange,
    toRequestWindow,
} from './odAnalyticsUtils';
import { buildTripExportRows, tripExportFileName } from './odTripExport';
import OdSelect from './components/OdSelect';
import OdExportDialog from './components/OdExportDialog';
import OverviewView from './views/OverviewView';
import DayboardView from './views/DayboardView';
import RouteMapView from './views/RouteMapView';
import OdPairsView from './views/OdPairsView';
import SafetyView from './views/SafetyView';
import IdleView from './views/IdleView';
import LedgerView from './views/LedgerView';
import './ODAnalytics.css';

const VIEWS = {
    OVERVIEW: 1,
    DAYBOARD: 2,
    ROUTE_MAP: 3,
    OD_PAIRS: 4,
    SAFETY: 5,
    IDLE: 6,
    LEDGER: 7,
};

// Matches the form-control style every other Horizon input uses (DateRangePicker's
// inputStyle): 44px tall, 8px radius, Gray/300 border, shadow-xs.
const assetInputSx = {
    minWidth: '22rem',
    '& .MuiOutlinedInput-root': {
        minHeight: '2.75rem',
        borderRadius: '0.5rem',
        background: '#FFFFFF',
        boxShadow: '0rem 0.0625rem 0.125rem rgba(16, 24, 40, 0.05)',
        fontFamily: 'Inter',
        fontSize: '0.875rem',
        color: '#101828',
        padding: '0.25rem 0.5rem',
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D0D5DD' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#D0D5DD' },
    '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#73A2BA', borderWidth: '0.0625rem' },
    '& .MuiInputBase-input::placeholder': { color: '#667085', opacity: 1 },
};

const chipSx = {
    height: '1.5rem',
    borderRadius: '999px',
    background: '#D0E0E8',
    color: '#26404E',
    fontFamily: 'Inter',
    fontWeight: 500,
    fontSize: '0.75rem',
    '& .MuiChip-deleteIcon': { color: '#26404E', fontSize: '1rem' },
};

const spinnerTheme = createTheme({ palette: { amcolor: { main: '#365A6D' } } });

const ODAnalytics = () => {
    const { apiResponse } = useApiData();
    const token = Cookies.get('jwtToken');
    const agencyID = Cookies.get('agencyUUID');
    const agencyIdSlug = Cookies.get('agency-id');
    const [navigtionClick, setNavigtionClick, navigationState, setNavigationState] = useOutletContext();

    // Agency data is authoritative for report timestamps — never the user profile.
    // When it cannot be resolved, fall back to the browser zone rather than UTC: this
    // window builds request boundaries, and UTC would silently shift them by the agency's
    // offset. The browser zone reproduces the behaviour this panel shipped with.
    const agencyTimezone = resolveAgencyTimezone({
        privileges: localStorage.getItem('listAgency'),
        agencyUuid: agencyID,
        agencyId: agencyIdSlug,
        persistedTimezone: Cookies.get(AGENCY_TIMEZONE_COOKIE),
    }) || moment.tz.guess();

    // ---- filter state -------------------------------------------------------

    const [date, setDate] = useState([]);
    const [inputValue, setInputValue] = useState('Select Dates');
    const [minDateRange, setMinDateRange] = useState(new DateObject().subtract(1, 'year'));
    const [maxDateRange, setMaxDateRange] = useState(new Date());
    const [dateRangeLabel, setDateRangeLabel] = useState('');

    const [assetOptions, setAssetOptions] = useState([]);
    const [selectedAssets, setSelectedAssets] = useState([]);
    const [assetSearch, setAssetSearch] = useState('');
    const [loadingAssets, setLoadingAssets] = useState(false);

    // ---- result state -------------------------------------------------------

    const [results, setResults] = useState(null);
    // Held unmerged so the asset gate can re-merge a single asset's slice. The Idle and Ledger
    // tiles read server rollups, which cannot be narrowed once summed — only re-summed from fewer.
    const [analyticsResults, setAnalyticsResults] = useState(null);
    // Which fan-out is allowed to publish its results — see generate() and reset().
    const requestSeq = useRef(0);
    // Per-selection, so it belongs at the top with the other fetches — unlike the tape's scrub
    // position, which changes ~12 times a second and stays inside RouteMapView.
    const [trail, setTrail] = useState(null);
    const [trailLoading, setTrailLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [progress, setProgress] = useState({ done: 0, total: 0 });
    const [requestWindow, setRequestWindow] = useState(null);

    const [notice, setNotice] = useState(null);
    const [exportOpen, setExportOpen] = useState(false);

    // ---- view + cross-view filters -----------------------------------------

    const [activeView, setActiveView] = useState(VIEWS.OVERVIEW);
    const [dayFilter, setDayFilter] = useState(null);
    const [brushRange, setBrushRange] = useState(null);
    const [pairFilter, setPairFilter] = useState(null);
    const [assetFilter, setAssetFilter] = useState(null);
    const [selectedTripId, setSelectedTripId] = useState(null);
    // The point pinned from a Safety or Idle row. Holds the popup's wording as well as the
    // coordinates, because the row that raised it is the only place those figures are known.
    const [focusPoint, setFocusPoint] = useState(null);
    const [hoverTripId, setHoverTripId] = useState(null);
    const [mapTheme, setMapTheme] = useState(defaultMapTheme);
    const [showTripNumbers, setShowTripNumbers] = useState(false);

    const searchTimer = useRef(null);

    // ---- asset lookup -------------------------------------------------------

    const searchAssets = useCallback(async (searchKey) => {
        try {
            const result = await APIGetDevices(setLoadingAssets, token, agencyID, {
                pageNumber: 1,
                totalPerPage: 100,
                searchKey: searchKey || '',
            });
            if (result?.data?.status === 200 && Array.isArray(result.data.data)) {
                const assets = result.data.data.map((device) => ({
                    uuid: device.oid,
                    id: device.assetName,
                    name: device.deviceSerial || '',
                }));
                setAssetOptions(_.sortBy(assets, 'id'));
            } else {
                setAssetOptions([]);
            }
        } catch (error) {
            setAssetOptions([]);
        } finally {
            setLoadingAssets(false);
        }
    }, [token, agencyID]);

    useEffect(() => {
        setNavigtionClick(5);
        setNavigationState('insights');
    }, []);

    // Debounce the asset search by 500ms, matching the previous panel's behaviour.
    useEffect(() => {
        if (!token) return undefined;
        if (searchTimer.current) clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => searchAssets(assetSearch), 500);
        return () => clearTimeout(searchTimer.current);
    }, [assetSearch, token, searchAssets]);

    // ---- generate -----------------------------------------------------------

    const generateDisabled = date.length < 2 || selectedAssets.length === 0 || loading;

    /**
     * `keepAsset` is what separates the map's "Reset view" from a new report. The asset is not a
     * cross-filter like the others — it is which asset the tab is *of*, so dropping it there sent
     * the reader back to the picker to answer a question they had already answered.
     */
    const clearCrossFilters = (keepAsset) => {
        setDayFilter(null);
        setBrushRange(null);
        setPairFilter(null);
        if (!keepAsset) setAssetFilter(null);
        setSelectedTripId(null);
        setHoverTripId(null);
        setFocusPoint(null);
    };

    const generate = async () => {
        const requested = toRequestWindow(date, agencyTimezone);
        if (!requested) {
            setNotice({
                kind: 'error',
                title: 'Select a valid date range',
                subtitle: 'Pick a start and an end date, then generate the report again.',
            });
            return;
        }
        const { fromTime, toTime } = requested;

        // Every fan-out claims a number, and only the newest one is allowed to publish. A
        // 10-asset report is slow enough to hit Reset — or Generate again — while it is in
        // flight, and the abandoned response used to land on top of a cleared panel, with
        // `date` and `selectedAssets` describing a different query than the table showed.
        const seq = requestSeq.current + 1;
        requestSeq.current = seq;

        setLoading(true);
        setProgress({ done: 0, total: selectedAssets.length });
        clearCrossFilters();

        // Safety and Idle are the only consumers of the analytics endpoint and both sit
        // behind the flag, so the telemetry scan is skipped entirely when they are hidden.
        // Same window, same assets, run alongside the trip fan-out rather than after it.
        const [fetched, analyticsFetched] = await Promise.all([
            fetchTripsForAssets(selectedAssets, {
                token,
                agencyId: agencyID,
                fromTime,
                toTime,
                onProgress: (done, total) => setProgress({ done, total }),
            }),
            OD_TELEMETRY_ENGINES_ENABLED
                ? fetchAnalyticsForAssets(selectedAssets, {
                    token, agencyId: agencyID, fromTime, toTime,
                })
                : Promise.resolve([]),
        ]);

        if (seq !== requestSeq.current) return;

        setResults(fetched);
        setAnalyticsResults(analyticsFetched);
        setRequestWindow({ fromTime, toTime });
        setLoading(false);

        const failures = failedAssets(fetched);
        const totalTrips = flattenTrips(fetched).length;

        if (failures.length > 0) {
            setNotice({
                kind: 'error',
                title: failures.length === selectedAssets.length
                    ? 'Unable to generate report'
                    : `${failures.length} of ${selectedAssets.length} assets failed`,
                // Show the server's actual reason — the 31-day cap and agency-ownership
                // rejections both used to disappear behind a generic message.
                subtitle: failures.map((f) => `${f.name}: ${f.error}`).join(' · '),
            });
        } else if (totalTrips === 0) {
            setNotice({
                kind: 'info',
                title: 'No trips found',
                subtitle: 'The request succeeded but no trips fall in this window. Try a wider date range or a different asset.',
            });
        } else {
            setActiveView(VIEWS.OVERVIEW);
        }
    };

    const reset = () => {
        // Orphans whatever is in flight, then stops the spinner it would have stopped itself.
        requestSeq.current += 1;
        setLoading(false);
        setProgress({ done: 0, total: 0 });
        setDate([]);
        setInputValue('Select Dates');
        setMinDateRange(new DateObject().subtract(1, 'year'));
        setMaxDateRange(new Date());
        setDateRangeLabel('');
        setSelectedAssets([]);
        setAssetSearch('');
        setResults(null);
        setAnalyticsResults(null);
        setTrail(null);
        setRequestWindow(null);
        clearCrossFilters();
        setActiveView(VIEWS.OVERVIEW);
    };

    // ---- derived data -------------------------------------------------------

    const allTrips = useMemo(() => flattenTrips(results), [results]);

    /**
     * Trips after every cross-view filter *except* the asset gate.
     *
     * Split out because the export scopes its own asset. The gate answers "which asset is this tab
     * showing", and on every tab but the Overview it holds one — so an export reading the gated
     * list could only ever offer the one asset already on screen, which is not a choice.
     */
    const filteredTrips = useMemo(() => {
        let list = filterTripsByDay(allTrips, dayFilter, agencyTimezone);
        list = filterTripsByRange(list, brushRange);
        list = filterTripsByPair(list, pairFilter);
        return list;
    }, [allTrips, dayFilter, brushRange, pairFilter, agencyTimezone]);

    /**
     * The Dayboard's own list: every cross-filter except the brush, which the Dayboard is where the
     * reader draws. A chart fed its own selection redraws every unbrushed day at zero, erasing the
     * context the brush was drawn against and leaving a second brush nothing to aim at — and the
     * table kept listing those days anyway, seeded from analytics rows the brush never touched, as
     * `trips: 0` beside a full day of idling.
     */
    const dayboardTrips = useMemo(() => {
        const list = filterTripsByPair(
            filterTripsByDay(allTrips, dayFilter, agencyTimezone),
            pairFilter,
        );
        return assetFilter ? list.filter((trip) => trip.assetName === assetFilter) : list;
    }, [allTrips, dayFilter, pairFilter, assetFilter, agencyTimezone]);

    /** Trips after every active cross-view filter. */
    const visibleTrips = useMemo(
        () => (assetFilter
            ? filteredTrips.filter((trip) => trip.assetName === assetFilter)
            : filteredTrips),
        [filteredTrips, assetFilter],
    );

    const selectedTrip = useMemo(
        () => visibleTrips.find((trip) => trip.startTraceId === selectedTripId) || null,
        [visibleTrips, selectedTripId],
    );

    const hasResults = Array.isArray(results) && allTrips.length > 0;

    /** The assets this report actually returned trips for — what the gate offers. */
    const reportAssets = useMemo(
        () => [...new Set(allTrips.map((trip) => trip.assetName))].sort(),
        [allTrips],
    );

    /** The whole fan-out. The Overview is the fleet table, so its rows always read every asset. */
    const fleetAnalytics = useMemo(() => mergeAnalytics(analyticsResults), [analyticsResults]);

    /**
     * Merged from the asset's own slice rather than filtered after the fact, so the tiles follow
     * the table: the Idle and Ledger rollups are server sums, and narrowing their rows alone would
     * leave "Total idle" reporting the fleet under a one-asset list.
     */
    const analytics = useMemo(() => (assetFilter
        ? mergeAnalytics((analyticsResults || [])
            .filter((entry) => (entry.asset?.id || '-') === assetFilter))
        : fleetAnalytics), [analyticsResults, assetFilter, fleetAnalytics]);

    // Analytics failures stay inside Safety and Idle rather than raising the report-level
    // snackbar — the trip views are unaffected, and a 413 there says "narrow the window",
    // which only makes sense next to the table it applies to.
    const analyticsError = analytics?.errors?.length > 0
        ? analytics.errors.map((failure) => `${failure.name}: ${failure.error}`).join(' · ')
        : null;

    /**
     * One trail per selected trip. Gated on the tape's flag so a build without the tape never pays
     * for a telemetry scan, and stale responses are ignored rather than cancelled — the request is
     * cheap and AbortController would have to thread through FetchAPI for no behavioural gain.
     */
    useEffect(() => {
        if (!OD_TRIP_TAPE_ENABLED || !selectedTrip) {
            setTrail(null);
            setTrailLoading(false);
            return undefined;
        }

        let stale = false;
        setTrailLoading(true);
        fetchTrailForTrip(selectedTrip, { token, agencyId: agencyID }).then((result) => {
            if (stale) return;
            setTrail(result);
            setTrailLoading(false);
        });
        return () => { stale = true; };
    }, [selectedTrip, token, agencyID]);

    // A trip selected before a filter narrowed it away would leave the deck showing a
    // trip that is no longer on the map.
    useEffect(() => {
        if (selectedTripId && !selectedTrip) setSelectedTripId(null);
    }, [selectedTripId, selectedTrip]);

    // ---- navigation ---------------------------------------------------------

    const insightsNavigationList = [
        {
            text: 'Asset Performance',
            value: 1,
            path: '/insights/assetPerformance',
            enabled: (apiResponse?.role === 'Agency Administrator' || apiResponse?.role === 'Agency Owner')
                ? true : apiResponse?.module?.insights?.assetPerformance,
        },
        {
            text: 'O/D Analytics',
            value: 5,
            path: '/insights/odAnalytics',
            enabled: (apiResponse?.role === 'Agency Administrator' || apiResponse?.role === 'Agency Owner')
                ? true : apiResponse?.module?.insights?.odAnalytics,
        },
        {
            text: 'Maintenance',
            value: 6,
            path: '/insights/maintenance',
            enabled: (apiResponse?.role === 'Agency Administrator' || apiResponse?.role === 'Agency Owner')
                ? true : apiResponse?.module?.insights?.assetPerformance,
        },
    ];

    const viewTabs = [
        { text: 'Overview', value: VIEWS.OVERVIEW, enabled: true },
        { text: 'Dayboard', value: VIEWS.DAYBOARD, enabled: true },
        { text: 'Route Map', value: VIEWS.ROUTE_MAP, enabled: true },
        { text: 'O/D Pairs', value: VIEWS.OD_PAIRS, enabled: true },
        { text: 'Safety', value: VIEWS.SAFETY, enabled: OD_TELEMETRY_ENGINES_ENABLED },
        { text: 'Idle', value: VIEWS.IDLE, enabled: OD_TELEMETRY_ENGINES_ENABLED },
        { text: 'Energy ledger', value: VIEWS.LEDGER, enabled: OD_TELEMETRY_ENGINES_ENABLED },
    ];

    // ---- export -------------------------------------------------------------

    /**
     * The fleet console's trip export, for whichever asset the dialog asked for.
     *
     * One export shape on every tab rather than four that followed the active view: the tab is
     * how the report is *read*, and a reader who wants the Dayboard's rollup on the Dayboard tab
     * still wants trips when they open a spreadsheet. `assetName` is `''` for the whole report.
     */
    const runExport = (assetName) => {
        // The dialog's choice replaces the Asset gate rather than narrowing under it: the day,
        // window and pair filters still apply, but the asset asked for here is the asset exported,
        // whichever one the tab happens to be open on.
        const trips = assetName
            ? filteredTrips.filter((trip) => trip.assetName === assetName)
            : filteredTrips;

        // Merged from the chosen asset's own slice, the same way the tiles narrow: the analytics
        // rows are keyed by tripId, so a fleet-wide payload would fold another asset's harsh
        // events onto nothing and cost a scan of rows this file will never use.
        const payload = assetName
            ? mergeAnalytics((analyticsResults || [])
                .filter((entry) => (entry.asset?.id || '-') === assetName))
            : fleetAnalytics;

        const rows = buildTripExportRows(trips, {
            analytics: OD_TELEMETRY_ENGINES_ENABLED ? payload : null,
            timezone: agencyTimezone,
        });

        setExportOpen(false);

        if (rows.length === 0) {
            setNotice({
                kind: 'info',
                title: 'Nothing to export',
                subtitle: assetName
                    ? `No trips for ${assetName} under the current filters. Clear a filter or pick another asset.`
                    : 'The current filters leave no trips. Clear a filter and try again.',
            });
            return;
        }

        downloadCSV(rows, tripExportFileName(rows));
    };

    // ---- filter chips -------------------------------------------------------

    const activeChips = [
        dayFilter && { key: 'day', label: `Day: ${dayFilter}`, clear: () => setDayFilter(null) },
        brushRange && {
            key: 'range',
            label: `Window: ${moment.tz(brushRange.min, agencyTimezone).format('D MMM')} – ${moment.tz(brushRange.max, agencyTimezone).format('D MMM')}`,
            clear: () => setBrushRange(null),
        },
        pairFilter && {
            key: 'pair',
            label: `Pair: ${pairFilter.origin} → ${pairFilter.destination}`,
            clear: () => setPairFilter(null),
        },
    ].filter(Boolean);

    // ---- render -------------------------------------------------------------

    /**
     * The one place the asset changes. Whatever was pinned or selected belonged to the asset being
     * left, so both go — everything else is derived and follows on its own.
     */
    const pickAsset = (assetName) => {
        setAssetFilter(assetName || null);
        setSelectedTripId(null);
        setFocusPoint(null);
    };

    const openAssetOnMap = (assetId) => {
        const row = (results || []).find((result) => (result.asset?.uuid || result.asset?.id) === assetId);
        pickAsset(row?.asset?.id);
        setActiveView(VIEWS.ROUTE_MAP);
    };

    const openDayOnMap = (day) => {
        setDayFilter(day);
        setSelectedTripId(null);
        if (day) setActiveView(VIEWS.ROUTE_MAP);
    };

    const openPairOnMap = (pair) => {
        setPairFilter(pair);
        setSelectedTripId(null);
        if (pair) setActiveView(VIEWS.ROUTE_MAP);
    };

    /**
     * A Safety or Idle row → the Route Map, framed on that row's own position.
     *
     * fleetconsole's `focusEvent`/`pinOnMap`. Selecting the row's trip is what makes the point
     * readable *in context* — the route it happened on is drawn, and the overlays narrow to that
     * trip. An episode outside any trip clears the selection instead of leaving the point floating
     * over whichever route happened to be selected, which would attribute it to the wrong journey.
     */
    const openPointOnMap = (kind) => (row) => {
        // Numbered over the visible list, exactly as the rail and the hover card number it —
        // the server's own tripNo indexes the whole window and diverges under a cross-filter.
        const focus = buildFocus(
            row, kind, agencyTimezone, buildTripNumbers(visibleTrips).get(row?.tripId),
        );
        if (!focus) {
            setNotice({
                kind: 'error',
                title: 'No position for this row',
                subtitle: 'The device recorded no coordinates here, so there is nothing to show on the map.',
            });
            return;
        }
        setSelectedTripId(focus.tripId);
        setFocusPoint(focus);
        setActiveView(VIEWS.ROUTE_MAP);
    };

    /**
     * A ledger row → the map, the way fleetconsole's own ledger table behaves: a trip row selects
     * its trip, and an event row flies to its point.
     *
     * The kinds map onto the two focus descriptors that already exist — a refuel and a drain are a
     * `fuel` popup, a session is a `charge` one. `type` is restored from `kind` because the ledger
     * renames REFILL to REFUEL for the reader, while `buildFocus` titles off the engine's own name.
     */
    const openLedgerEntry = (entry) => {
        if (!entry) return;
        if (entry.kind === 'TRIP') {
            setSelectedTripId(entry.tripId ?? null);
            // A trip row frames the whole route; a pinned point from a previous row would keep the
            // map zoomed on somewhere that trip merely passed.
            setFocusPoint(null);
            setActiveView(VIEWS.ROUTE_MAP);
            return;
        }
        if (entry.kind === 'CHARGE') {
            openPointOnMap('charge')(entry);
            return;
        }
        openPointOnMap('fuel')({ ...entry, type: entry.kind === 'DRAIN' ? 'DRAIN' : 'REFILL' });
    };

    const renderView = () => {
        if (loading) {
            return (
                <div className="od-view-loading">
                    <ThemeProvider theme={spinnerTheme}>
                        <CircularProgress color="amcolor" />
                    </ThemeProvider>
                </div>
            );
        }

        if (!hasResults) {
            return (
                <div className="od-card">
                    <div className="od-empty">
                        <div className="od-empty-title">
                            {results ? 'No trips in this window' : 'Choose a timeline and up to 10 assets'}
                        </div>
                        <div className="od-empty-text">
                            {results
                                ? 'The request succeeded but returned no trips. Try a wider date range or a different asset.'
                                : 'Pick a date range and one or more assets, then generate the report to see movement patterns, daily activity and origin/destination pairs.'}
                        </div>
                    </div>
                </div>
            );
        }

        // Every tab but the Overview reads as one asset's story — a route, a day, a ledger. With
        // several assets in the report and none picked they silently blend, and the reader has no
        // way to tell whose figures they are looking at. fleetconsole gates the same way: the
        // Overview is the fleet table, and you pick a row out of it before anything else opens.
        if (activeView !== VIEWS.OVERVIEW && !assetFilter && reportAssets.length > 1) {
            return (
                <div className="od-card">
                    <div className="od-empty">
                        <div className="od-empty-title">Select an asset</div>
                        <div className="od-empty-text">
                            {`This report covers ${reportAssets.length} assets. Choose one from the Asset selector above to open this tab, or go back to the Overview for the fleet summary.`}
                        </div>
                    </div>
                </div>
            );
        }

        switch (activeView) {
            case VIEWS.DAYBOARD:
                return (
                    <DayboardView
                      trips={dayboardTrips}
                      analyticsLoaded={Boolean(analytics?.loaded)}
                      episodes={analytics?.episodes}
                      events={analytics?.events}
                      fuelEvents={analytics?.fuelEvents}
                      fuelTrips={analytics?.fuelTrips}
                      speedTrips={analytics?.speedTrips}
                      fromTime={requestWindow?.fromTime}
                      toTime={requestWindow?.toTime}
                      timezone={agencyTimezone}
                      dayFilter={dayFilter}
                      onSelectDay={openDayOnMap}
                      dateRange={brushRange}
                      onBrushRange={setBrushRange}
                    />
                );
            case VIEWS.ROUTE_MAP:
                return (
                    <RouteMapView
                      trips={visibleTrips}
                      totalTripCount={allTrips.length}
                      selectedTrip={selectedTrip}
                      onSelectTrip={(trip) => {
                          setSelectedTripId(trip?.startTraceId ?? null);
                          // Picking another trip abandons the pinned point — otherwise the map
                          // stays framed on a place that belongs to the trip you just left.
                          setFocusPoint(null);
                      }}
                      hoverTripId={hoverTripId}
                      onHoverTrip={setHoverTripId}
                      timezone={agencyTimezone}
                      events={analytics?.events}
                      episodes={analytics?.episodes}
                      fuelEvents={analytics?.fuelEvents}
                      chargeSessions={analytics?.chargeSessions}
                      trail={trail}
                      trailLoading={trailLoading}
                      focus={focusPoint}
                      onClearFocus={() => setFocusPoint(null)}
                      onFocusEvent={openPointOnMap('event')}
                      onFocusEpisode={openPointOnMap('idle')}
                      onFocusFuel={openPointOnMap('fuel')}
                      onFocusCharge={openPointOnMap('charge')}
                      mapTheme={mapTheme}
                      onToggleMapTheme={(event) => setMapTheme(
                          event.target.checked ? MAP_THEMES.SATELLITE : MAP_THEMES.MONOCHROME,
                      )}
                      showTripNumbers={showTripNumbers}
                      onToggleTripNumbers={(event) => setShowTripNumbers(event.target.checked)}
                      // Drops the selection and every cross-filter but the asset, and the map's own
                      // fit effect reframes from the widened trip set that follows. Passed as an
                      // arrow, not bare: the click event would arrive as a truthy `keepAsset`.
                      onResetView={() => clearCrossFilters(true)}
                      canResetView={Boolean(
                          selectedTripId || dayFilter || brushRange || pairFilter,
                      )}
                    />
                );
            case VIEWS.OD_PAIRS:
                return (
                    <OdPairsView
                      trips={visibleTrips}
                      fuelTrips={analytics?.fuelTrips}
                      pairFilter={pairFilter}
                      onSelectPair={openPairOnMap}
                    />
                );
            case VIEWS.SAFETY:
                return (
                    <SafetyView
                      events={analytics?.events}
                      availability={analytics?.eventsAvailability}
                      speedTrips={analytics?.speedTrips}
                      trips={visibleTrips}
                      timezone={agencyTimezone}
                      error={analyticsError}
                      tooLarge={analytics?.tooLarge}
                      onFocusEvent={openPointOnMap('event')}
                    />
                );
            case VIEWS.IDLE:
                return (
                    <IdleView
                      episodes={analytics?.episodes}
                      policy={analytics?.policy}
                      policyByAsset={analytics?.policyByAsset}
                      rollup={analytics?.rollup}
                      availability={analytics?.idleAvailability}
                      trips={visibleTrips}
                      timezone={agencyTimezone}
                      error={analyticsError}
                      tooLarge={analytics?.tooLarge}
                      onFocusEpisode={openPointOnMap('idle')}
                    />
                );
            case VIEWS.LEDGER:
                return (
                    <LedgerView
                      entries={analytics?.ledgerEntries}
                      assets={analytics?.ledgerAssets}
                      rollup={analytics?.ledgerRollup}
                      availability={analytics?.ledgerAvailability}
                      trips={visibleTrips}
                      timezone={agencyTimezone}
                      error={analyticsError}
                      tooLarge={analytics?.tooLarge}
                      onOpenEntry={openLedgerEntry}
                    />
                );
            case VIEWS.OVERVIEW:
            default:
                return (
                    <OverviewView
                      results={results}
                      trips={visibleTrips}
                      // Unscoped: the Overview is the fleet table, so it wants every asset's
                      // events, episodes and drains and groups them per row itself. Handed the
                      // scoped merge, every row but the picked one would report zero.
                      fuelTrips={fleetAnalytics?.fuelTrips}
                      analytics={fleetAnalytics}
                      selectedAsset={assetFilter}
                      onSelectAsset={openAssetOnMap}
                    />
                );
        }
    };

    return (
        <div className="asset-performance-container od-analytics-container">
            <ListNavigationTab
              list={insightsNavigationList}
              navigtionClick={navigtionClick}
              setNavigtionClick={setNavigtionClick}
            />

            <div className="asset-performance-header">
                <Title
                  title="O/D Analytics"
                  subtitle="Monitor and analyse origin / destination movement patterns across your assets"
                  titleSize="1.875rem"
                  subtitleSize="1rem"
                  lineHeight="2.375rem"
                  subtitleLineHeight="1.5rem"
                />
            </div>

            <div className="asset-performance-body od-body">
                <div className="od-filter-card">
                    <div className="od-filter-fields">
                        <div className="od-filter-field">
                            <span className="od-filter-label">Timeline</span>
                            <DateRangePicker
                              date={date}
                              setDate={setDate}
                              inputValue={inputValue}
                              setInputValue={setInputValue}
                              minDateRange={minDateRange}
                              setMinDateRange={setMinDateRange}
                              maxDateRange={maxDateRange}
                              setMaxDateRange={setMaxDateRange}
                              setApplyDatePicker={() => {}}
                              dateRange={dateRangeLabel}
                              setDateRange={setDateRangeLabel}
                            />
                        </div>
                        <div className="od-filter-field">
                            <span className="od-filter-label">{`Assets (up to ${OD_MAX_ASSETS})`}</span>
                            <Autocomplete
                              multiple
                              disableCloseOnSelect
                              limitTags={2}
                              size="small"
                              options={assetOptions}
                              value={selectedAssets}
                              loading={loadingAssets}
                              onChange={(_event, value) => setSelectedAssets(value.slice(0, OD_MAX_ASSETS))}
                              onInputChange={(_event, value) => setAssetSearch(value)}
                              getOptionLabel={(option) => option.id || ''}
                              isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
                              getOptionDisabled={() => selectedAssets.length >= OD_MAX_ASSETS}
                              renderTags={(value, getTagProps) => value.map((option, index) => (
                                  <Chip
                                    label={option.id}
                                    size="small"
                                    sx={chipSx}
                                    {...getTagProps({ index })}
                                    key={option.uuid}
                                  />
                              ))}
                              renderOption={(optionProps, option) => (
                                  <li {...optionProps} key={option.uuid}>
                                      <div className="od-cell-stack">
                                          <span className="od-cell-strong">{option.id}</span>
                                          {option.name && <span className="od-cell-muted">{option.name}</span>}
                                      </div>
                                  </li>
                              )}
                              renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    placeholder={selectedAssets.length === 0 ? 'Select assets / devices' : ''}
                                    sx={assetInputSx}
                                  />
                              )}
                            />
                        </div>
                    </div>
                    <div className="od-filter-actions">
                        <LiveMapButton label="Reset" onClickFunction={reset} />
                        {hasResults && <ExportCSVButton label="Export CSV" buttonHeight="2.5rem" onClickFunction={() => setExportOpen(true)} />}
                        <CreateWithIcon
                          module="assetPerformance"
                          label={loading && progress.total > 1
                              ? `Loading ${progress.done}/${progress.total}`
                              : 'Generate Report'}
                          buttonWidth="10.5rem"
                          onClickFunction={generate}
                          disable={generateDisabled}
                          loading={loading}
                        />
                    </div>
                    {selectedAssets.length >= OD_MAX_ASSETS && (
                        <div className="od-filter-hint">
                            {`Showing the maximum of ${OD_MAX_ASSETS} assets. Trips are fetched one request per asset, so the cap keeps the report responsive.`}
                        </div>
                    )}
                </div>

                {hasResults && (reportAssets.length > 1 || activeChips.length > 0) && (
                    <div className="od-filter-chips">
                        {reportAssets.length > 1 && (
                            <OdSelect
                              label="Asset"
                              value={assetFilter || ''}
                              onChange={pickAsset}
                              ariaLabel="Asset shown in this report"
                              options={[
                                  { value: '', label: 'All assets' },
                                  ...reportAssets.map((name) => ({ value: name, label: name })),
                              ]}
                            />
                        )}
                        {activeChips.map((chip) => (
                            <span className="od-chip" key={chip.key}>
                                {chip.label}
                                <button
                                  type="button"
                                  className="od-chip-clear"
                                  aria-label={`Clear ${chip.label}`}
                                  onClick={chip.clear}
                                >
                                    ×
                                </button>
                            </span>
                        ))}
                        {visibleTrips.length !== allTrips.length && (
                            <span className="od-chip od-chip-muted">
                                {`${visibleTrips.length} of ${allTrips.length} trips`}
                            </span>
                        )}
                    </div>
                )}

                <div className="od-viewtabs">
                    <ConfigTab
                      color="transparent"
                      types={viewTabs}
                      module="odAnalytics"
                      navigtionClick={activeView}
                      setNavigtionClick={setActiveView}
                      onTabClick={setActiveView}
                    />
                </div>

                {renderView()}
            </div>

            {exportOpen && (
                <OdExportDialog
                  assets={reportAssets}
                  trips={filteredTrips}
                  onCancel={() => setExportOpen(false)}
                  onExport={runExport}
                />
            )}

            {notice && (
                <div className="notification">
                    <SnackBar
                      state={Boolean(notice)}
                      setState={() => setNotice(null)}
                      title={notice.title}
                      subtitile={notice.subtitle}
                      module="AssetPerformance"
                      action={notice.kind === 'error' ? 'AssetPerformance' : 'info'}
                    />
                </div>
            )}
        </div>
    );
};

export default ODAnalytics;
